import { type Font } from 'satori';

const SATORI_EMOJI_LANGUAGE_CODE = 'emoji';

const MIN_EMOJI_RASTER_PX = 64;
const MAX_EMOJI_RASTER_PX = 512;
const SYSTEM_EMOJI_FONT_STACK = '"Apple Color Emoji","Segoe UI Emoji","Noto Color Emoji",sans-serif';
// Color-bitmap emoji glyphs overflow the em-square; without this, fillText clips the edges.
const EMOJI_OVERFLOW_PAD_RATIO = 0.125;

const emojiDataUrlCache = new Map<string, string>();

const NO_ADDITIONAL_FONTS: Font[] = [];

/** Scale the raster with font size so large type stays sharp, then clamp for memory. */
export const getEmojiRasterPx = (fontSize: number): number =>
  Math.min(MAX_EMOJI_RASTER_PX, Math.max(MIN_EMOJI_RASTER_PX, Math.ceil(fontSize * 2)));

export const getEmojiOverflowPadPx = (rasterPx: number): number => Math.ceil(rasterPx * EMOJI_OVERFLOW_PAD_RATIO);

const rasterizeEmojiToDataUrl = (emoji: string, rasterPx: number): string => {
  const cacheKey = `${rasterPx}:${emoji}`;
  const cached = emojiDataUrlCache.get(cacheKey);

  if (cached) {
    return cached;
  }

  const pad = getEmojiOverflowPadPx(rasterPx);
  const canvasPx = rasterPx + pad * 2;
  const canvas = document.createElement('canvas');
  canvas.width = canvasPx;
  canvas.height = canvasPx;
  const ctx = canvas.getContext('2d');

  if (!ctx) {
    return '';
  }

  const center = canvasPx / 2;
  ctx.font = `${rasterPx}px ${SYSTEM_EMOJI_FONT_STACK}`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(emoji, center, center);

  const dataUrl = canvas.toDataURL('image/png');
  emojiDataUrlCache.set(cacheKey, dataUrl);

  return dataUrl;
};

/**
 * Satori only draws glyphs from the fonts we pass (Inter, etc.). Those have no
 * emoji coverage, so missing characters become empty boxes in the export.
 * Returning a PNG data URL tells satori to place the system emoji instead.
 */
export const loadSatoriEmojiAsset = async (
  languageCode: string,
  segment: string,
  fontSize: number,
): Promise<string | Font[]> => {
  if (languageCode !== SATORI_EMOJI_LANGUAGE_CODE) {
    return NO_ADDITIONAL_FONTS;
  }

  const dataUrl = rasterizeEmojiToDataUrl(segment, getEmojiRasterPx(fontSize));

  if (!dataUrl) {
    return NO_ADDITIONAL_FONTS;
  }

  return dataUrl;
};
